/**
 * Eski tasarım tokenları (Görsel Dil v2, docs/design/final/design_handoff_icebrkr bölüm 4).
 * Mobil M0'dan beri buradan OKUMAZ (`./mobil-tema`); web tarafındaki eski tüketiciler için kalır.
 *
 * Önceki "Modernist" katmanı (radius 0, tek kırmızı accent, 2px ayırıcı) kaldırıldı:
 * yumuşatılmış köşeler, dört kademe derinlik, kimlik taşıyan petrol mavisi (birincil)
 * ile eylem taşıyan kiremit (vurgu) ayrımı, ve her ekranda görünür insan öğesi.
 *
 * Roller: birincil = krom (kimlik, seçim, bağlantı), vurgu = olumlu eylem, kırmızı = hata/olumsuz.
 *
 * Marka katmanı (README 4.10): ürün adı, domain, birincil ve vurgu rengi `brand`
 * altında toplanır; hiçbir ekrana sabitlenmez. Marka rengi değişirse ramp adımları
 * (hover/pressed/tint) tek tondan türetilir — `deriveScale`.
 *
 * Kontrast: açık ve karanlık paletteki metin/arka plan çiftleri WCAG AA eşiğini
 * geçecek şekilde seçildi; `contrast.spec.ts` bunu testle doğrular.
 */
import { controlDark, controlLight, onPrimaryDark } from './control-tokens';
import { layout } from './layout';
/**
 * Varsayılan marka — HALKA AÇIK AD (karar 2026-08-24, geçiş sözleşmesi
 * `docs/warmbreak-marka-sozlesmesi.md`). Kod adı `icebreaker` DAHİLİ kimlikte
 * (paket adları, bundle kimliği, şema adları) kalır ve değişmez (P9).
 *
 * Yazım TEK doğrudur: iç büyük harf. `Warmbreak`/`WARMBREAK` yasaktır ve marka
 * adı hiçbir yerde `text-transform`e girmez — `brand-name-embedding.guard.spec`
 * bunu makineyle denetler.
 */
export const defaultBrand = {
    name: 'WarmBreak',
    shortName: 'WarmBreak',
    domain: 'warmbreak.com',
    publicDomain: 'warmbreak.com',
    logoLightUrl: '/brand/warmbreak-light.svg',
    logoDarkUrl: '/brand/warmbreak-dark.svg',
    logoMonoUrl: '/brand/warmbreak-mono.svg',
    supportEmail: 'support@warmbreak.com',
    primary: '#1C6A9C',
    primaryContrast: '#FFFFFF',
    accent: '#B4551F',
    accentContrast: '#FFFFFF',
};
export function resolveBrand(overrides = {}) {
    return { ...defaultBrand, ...overrides };
}
/** Ham renk basamakları. Semantik isimler bunlara referans verir (README 4.1–4.3). */
export const palette = {
    /** Sıcak nötr aile: zemin kırık beyaz, saf beyaz yalnızca kart yüzeyinde. */
    neutral: {
        bg: '#F7F6F3',
        surface: '#FFFFFF',
        sunken: '#EFEDE8',
        line: '#E2DFD8',
        lineStrong: '#CFCBC2',
        lineStronger: '#BAB6B6', // bölüm ayırıcısı — ölçüm: `design/section-heading.ts`
        ink: '#1A2530',
        ink2: '#4E5B66',
        /**
         * README 4.1 bu rolü `#6E7A86` ile ve "4,6:1" notuyla veriyor; ölçüldüğünde
         * yüzey üzerinde 4,38:1 çıkıyor ve AA (4,5:1) eşiğini geçmiyor. Erişilebilirlik
         * kırmızı çizgi olduğu için ton, README'nin beyan ettiği orana ulaşan en yakın
         * değere indirildi (yüzey 5,53:1 · zemin 5,12:1 · gömülü 4,73:1).
         */
        ink3: '#5E6A75',
    },
    /** Birincil — petrol mavisi. Krom, kimlik, seçim, gerekçe. */
    primary: {
        100: '#E8F1F7',
        200: '#C9DEEC',
        500: '#1C6A9C',
        600: '#155580',
        700: '#0F4265',
    },
    /** Vurgu — kiremit. Yalnızca eylem ve başarı anı. */
    accent: {
        100: '#FBEDE3',
        200: '#F3D4BE',
        500: '#DA7A3E',
        600: '#B4551F',
        700: '#8E4217',
    },
    /** Semantik durumlar — kırmızı yalnızca burada. */
    state: {
        ok: '#17795B',
        okTint: '#E4F2EC',
        warn: '#8A5A0C',
        warnTint: '#F8F0DF',
        info: '#1C6A9C',
        infoTint: '#E8F1F7',
        err: '#B4231C',
        errTint: '#FAEAE7',
    },
    /** Karanlık tema — açığın koyulaştırılmışı değil, ayrı set (README 4.3). */
    dark: {
        bg: '#12181D',
        surface: '#1A2229',
        raised: '#222C34',
        line: '#2E3A44',
        lineStrong: '#3D4B57',
        ink: '#EDF1F4',
        ink2: '#AFBCC6',
        ink3: '#8695A1',
        primary: '#58A9D8',
        primaryTint: '#16303F',
        primaryBorder: '#1E4B63',
        accent: '#F08A4B',
        accentTint: '#33210F',
        /** Açık turuncu üzerinde beyaz AA vermez; dolgu üstü metin koyu ink olur. */
        onAccent: '#1A1008',
        ok: '#4FBF8B',
        warn: '#E0A641',
        info: '#7FB0DC',
        err: '#FF8A75',
        tagSurface: '#26313A',
    },
};
export const lightColors = {
    ...controlLight,
    background: palette.neutral.bg,
    surface: palette.neutral.surface,
    surfaceRaised: palette.neutral.surface,
    surfaceSunken: palette.neutral.sunken,
    border: palette.neutral.line,
    borderStrong: palette.neutral.lineStrong,
    sectionDivider: palette.neutral.lineStronger,
    textPrimary: palette.neutral.ink,
    textSecondary: palette.neutral.ink2,
    textTertiary: palette.neutral.ink3,
    textDisabled: palette.neutral.ink3,
    textOnPrimary: '#FFFFFF',
    textOnAccent: '#FFFFFF',
    primary: palette.primary[500],
    primaryHover: palette.primary[600],
    primaryActive: palette.primary[700],
    primarySubtle: palette.primary[100],
    primaryBorder: palette.primary[200],
    primaryStrong: palette.primary[700],
    accent: palette.accent[600],
    accentHover: palette.accent[700],
    accentSoft: palette.accent[500],
    accentSubtle: palette.accent[100],
    accentBorder: palette.accent[200],
    accentStrong: palette.accent[700],
    focusRing: palette.primary[500],
    focusRingOnInk: palette.dark.primary,
    success: palette.state.ok,
    successSubtle: palette.state.okTint,
    warning: palette.state.warn,
    warningSubtle: palette.state.warnTint,
    info: palette.state.info,
    infoSubtle: palette.state.infoTint,
    danger: palette.state.err,
    dangerSubtle: palette.state.errTint,
    overlay: 'rgba(26, 37, 48, 0.42)',
    skeleton: palette.neutral.sunken,
    surfaceWarm: palette.neutral.sunken,
    surfaceWarm2: palette.accent[100],
    qrSurface: '#FFFFFF',
    qrModule: palette.neutral.ink,
    chromeSurface: palette.dark.bg,
    chromeBorder: palette.dark.line,
    chromeText: palette.dark.ink,
    chromeTextMuted: palette.dark.ink2,
    chromeBrandSurface: palette.neutral.bg,
    chromeBrandInk: palette.dark.bg,
    chromeActive: palette.dark.primary,
};
export const darkColors = {
    ...controlDark,
    background: palette.dark.bg,
    surface: palette.dark.surface,
    surfaceRaised: palette.dark.raised,
    surfaceSunken: palette.dark.tagSurface,
    border: palette.dark.line,
    borderStrong: palette.dark.lineStrong,
    sectionDivider: palette.dark.lineStrong,
    textPrimary: palette.dark.ink,
    textSecondary: palette.dark.ink2,
    textTertiary: palette.dark.ink3,
    textDisabled: palette.dark.ink3,
    textOnPrimary: onPrimaryDark,
    textOnAccent: palette.dark.onAccent,
    primary: palette.dark.primary,
    primaryHover: '#7CBFE4',
    primaryActive: '#9AD1EE',
    primarySubtle: palette.dark.primaryTint,
    primaryBorder: palette.dark.primaryBorder,
    primaryStrong: '#BBD9EC',
    accent: palette.dark.accent,
    accentHover: '#F6A574',
    accentSoft: palette.dark.accent,
    accentSubtle: palette.dark.accentTint,
    accentBorder: '#5A3A1C',
    accentStrong: '#F6A574',
    focusRing: palette.dark.primary,
    focusRingOnInk: palette.dark.primary,
    success: palette.dark.ok,
    successSubtle: '#123328',
    warning: palette.dark.warn,
    warningSubtle: '#33280F',
    info: palette.dark.info,
    infoSubtle: palette.dark.primaryTint,
    danger: palette.dark.err,
    dangerSubtle: '#3A1C18',
    overlay: 'rgba(6, 10, 13, 0.66)',
    skeleton: palette.dark.raised,
    surfaceWarm: palette.dark.raised,
    surfaceWarm2: palette.dark.accentTint,
    // Karanlık temada da AÇIK zemin/KOYU modül — okunabilirlik marka kararının önündedir.
    qrSurface: '#FFFFFF',
    qrModule: palette.neutral.ink,
    /*
     * REFERANS (`Icebrkr Karanlık Tema - Ekranlar.dc.html:182`): karanlıkta bant
     * zemini SAYFA ZEMİNİNİN AYNISIDIR (`#12181D`), ayrımı 1px `#2E3A44` yapar.
     *
     * Bant bir kademe yükseltilirse (`raised` #222C34) krom, içerik kartlarından
     * (`surface` #1A2229) DAHA YUKARIDA görünür — Yön B'nin tezi tam tersidir:
     * kabuk geri çekilir, içerik öne çıkar. Bu bir renk nüansı değil derinlik
     * sırasıdır; referansa dönüldü.
     */
    chromeSurface: palette.dark.bg,
    chromeBorder: palette.dark.line,
    chromeText: palette.dark.ink,
    chromeTextMuted: palette.dark.ink2,
    chromeBrandSurface: palette.neutral.bg,
    chromeBrandInk: palette.dark.bg,
    chromeActive: palette.dark.primary,
};
/** Boşluk ölçeği — 4 / 8 / 12 / 16 / 24 / 32 (README 4.5). */
export const spacing = {
    none: 0,
    xxs: 2,
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
    '2xl': 32,
    '3xl': 48,
    '4xl': 64,
};
/**
 * Köşe ölçeği (README 4.5): 4 skeleton/ilerleme · 6 ilgi etiketi · 8 buton/input ·
 * 12 kart (varsayılan) · 16 modal/öne çıkan · 999 avatar ve durum rozeti.
 */
export const radius = {
    none: 0,
    xs: 4,
    sm: 6,
    md: 8,
    lg: 12,
    xl: 16,
    pill: 999,
};
/** Avatar ölçeği (README 4.6). Şekil anlam taşır: kişi daire, etkinlik/şirket köşeli. */
export const avatarSize = {
    xs: 26,
    sm: 36,
    md: 40,
    lg: 48,
    lgWide: 52, // geniş kolonlu kişi kartı — `Web Akış A` A2 §2 (48'lik `lg`nin geniş eşi)
    xl: 64,
    '2xl': 88,
};
export const typography = {
    fontFamily: {
        /** Arayüz — liste, buton, etiket, gövde. */
        sans: '"Instrument Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        /** Yalnızca başlık ve poster ölçeği. Listede, butonda, etikette KULLANILMAZ. */
        display: '"Source Serif 4", Georgia, "Times New Roman", serif',
        mono: '"JetBrains Mono", "SF Mono", Menlo, monospace',
    },
    /**
     * Ölçek: kicker/etiket 11 · meta 12 · gövde 14 · kişi adı 15 · kart başlığı 17 · bölüm
     * başlığı 22/24/30 · sayfa başlığı 36 · `hero` 44 (web giriş posteri, referans `AUTH` 395
     * 44/1.03, kullanıcı kararı 07.09.2026; giriş dışında kullanılmaz) · poster 48 · `micro` 10.
     * `sm` 13 → 14 ve `xl` 20 → 22 (manuel test bulgusu 4). Yetkili kaynak devir paketi v2
     * ölçeğidir (`Görsel Dil v2` → `typeScale`); sapan iki basamak referansın ALTINDAYDI.
     */
    fontSize: {
        micro: 10,
        kicker: 11,
        xs: 12,
        sm: 14,
        md: 15,
        lg: 17,
        xl: 22,
        '2xl': 24,
        '3xl': 30,
        '4xl': 36,
        hero: 44,
        '5xl': 48,
    },
    lineHeight: {
        hero: 1.03,
        /** Poster ve sayfa başlığı. */
        poster: 1.08,
        tight: 1.15,
        /** Kişi adı ve kart başlığı. */
        snug: 1.3,
        normal: 1.6,
        relaxed: 1.7,
    },
    fontWeight: {
        regular: '400',
        medium: '500',
        semibold: '600',
        bold: '700',
        /** Serif başlık üst ağırlığı — v2'de 800 yok. */
        heavy: '700',
    },
    letterSpacing: {
        /** Poster ve sayfa başlığı. */
        tight: '-0.02em',
        /** Bölüm başlığı. */
        snug: '-0.01em',
        normal: '0em',
        /** Kicker: uppercase + geniş aralık. */
        kicker: '0.06em',
        /**
         * KOD ALANI — kart kodu gibi HANE HANE okunan girdiler (Akış C · C-09
         * referansı: başlık ailesi + `letter-spacing:.12em`). Gövde metninde
         * KULLANILMAZ.
         */
        code: '0.12em',
    },
};
/** Derinlik — gölge rengi ink'in şeffaf hali, siyah değil (README 4.5). */
export const elevation = {
    none: 'none',
    xs: '0 1px 2px rgba(26, 37, 48, 0.06)',
    sm: '0 1px 2px rgba(26, 37, 48, 0.06), 0 2px 6px rgba(26, 37, 48, 0.06)',
    md: '0 2px 4px rgba(26, 37, 48, 0.05), 0 8px 20px rgba(26, 37, 48, 0.09)',
    lg: '0 4px 8px rgba(26, 37, 48, 0.06), 0 18px 44px rgba(26, 37, 48, 0.14)',
};
/** Karanlıkta derinlik gölgeyle değil yüzey kademesi + kenarlıkla kurulur. */
export const elevationDark = {
    none: 'none',
    xs: 'none',
    sm: '0 1px 2px rgba(0, 0, 0, 0.4)',
    md: '0 8px 20px rgba(0, 0, 0, 0.45)',
    lg: '0 18px 44px rgba(0, 0, 0, 0.55)',
};
export const motion = {
    duration: { instant: 0, fast: 120, normal: 200, slow: 320 },
    easing: {
        standard: 'cubic-bezier(0.2, 0, 0, 1)',
        decelerate: 'cubic-bezier(0, 0, 0, 1)',
        accelerate: 'cubic-bezier(0.3, 0, 1, 1)',
    },
    /** Prototipte ölçülen zamanlamalar (README 9). */
    timing: { skeletonMinMs: 400, listLoadMs: 950, requestSubmitMs: 900, shimmerMs: 1400 },
};
/** Erişilebilirlik eşikleri — tasarım sisteminde baştan tanımlı. */
export const a11y = {
    /** Mobil dokunma alanı en az 44×44 pt, web'de 40px. */
    minTouchTargetPt: 44,
    minTouchTargetWebPx: 40,
    minContrastNormalText: 4.5,
    minContrastLargeText: 3,
    minContrastUiComponent: 3,
    focusRingWidthPx: 2,
    focusRingOffsetPx: 2,
};
/** Bir tonu açar (amount > 0) veya koyulaştırır (amount < 0). */
function shade(hex, amount) {
    const value = parseInt(hex.replace('#', ''), 16);
    const channels = [(value >> 16) & 255, (value >> 8) & 255, value & 255].map((channel) => Math.max(0, Math.min(255, Math.round(amount < 0 ? channel * (1 + amount) : channel + (255 - channel) * amount))));
    return `#${channels.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
}
/**
 * Marka rengi değiştiğinde ramp adımlarını tek tondan türetir (README 4.10) —
 * etkileşim durumları düzleşmesin diye hover/pressed/tint hep ölçekten gelir.
 */
export function deriveScale(base, role) {
    return role === 'primary'
        ? {
            100: shade(base, 0.9),
            200: shade(base, 0.68),
            500: base,
            600: shade(base, -0.2),
            700: shade(base, -0.38),
        }
        : {
            100: shade(base, 0.9),
            200: shade(base, 0.7),
            500: shade(base, 0.22),
            600: base,
            700: shade(base, -0.22),
        };
}
function applyBrand(base, mode, brand) {
    // Karanlık temada ramp yön değiştirir; marka tonu bir adım açılarak taşınır.
    const primaryChanged = brand.primary !== defaultBrand.primary;
    const accentChanged = brand.accent !== defaultBrand.accent;
    if (!primaryChanged && !accentChanged)
        return base;
    const primary = deriveScale(brand.primary, 'primary');
    const accent = deriveScale(brand.accent, 'accent');
    return {
        ...base,
        ...(primaryChanged
            ? mode === 'light'
                ? {
                    primary: primary[500],
                    primaryHover: primary[600],
                    primaryActive: primary[700],
                    primarySubtle: primary[100],
                    primaryBorder: primary[200],
                    primaryStrong: primary[700],
                    focusRing: primary[500],
                    // İnk bandın halkası açık temada da KARANLIK ramp'tan gelir.
                    focusRingOnInk: shade(brand.primary, 0.42),
                }
                : {
                    primary: shade(brand.primary, 0.42),
                    primaryHover: shade(brand.primary, 0.58),
                    primaryActive: shade(brand.primary, 0.7),
                    primarySubtle: shade(brand.primary, -0.66),
                    primaryBorder: shade(brand.primary, -0.46),
                    primaryStrong: shade(brand.primary, 0.72),
                    focusRing: shade(brand.primary, 0.42),
                    focusRingOnInk: shade(brand.primary, 0.42),
                }
            : {}),
        ...(accentChanged
            ? mode === 'light'
                ? {
                    accent: accent[600],
                    accentHover: accent[700],
                    accentSoft: accent[500],
                    accentSubtle: accent[100],
                    accentBorder: accent[200],
                    accentStrong: accent[700],
                }
                : {
                    accent: shade(brand.accent, 0.44),
                    accentHover: shade(brand.accent, 0.58),
                    accentSoft: shade(brand.accent, 0.44),
                    accentSubtle: shade(brand.accent, -0.72),
                    accentBorder: shade(brand.accent, -0.5),
                    accentStrong: shade(brand.accent, 0.58),
                }
            : {}),
    };
}
export function createTheme(mode, brand = defaultBrand) {
    const base = mode === 'dark' ? darkColors : lightColors;
    return {
        mode,
        brand,
        colors: applyBrand(base, mode, brand),
        spacing,
        radius,
        avatarSize,
        typography,
        elevation: mode === 'dark' ? elevationDark : elevation,
        motion,
        a11y,
        layout,
    };
}
/** Bileşenlerin desteklemesi zorunlu durumlar. */
export const COMPONENT_STATES = [
    'default',
    'hover',
    'focus',
    'active',
    'disabled',
    'loading',
    'error',
];
//# sourceMappingURL=tokens.js.map